import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { ConfigContext } from 'contexts/ConfigContext';
import ModelSocials from 'components/Model/ModelSocials';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { getStyledName } from 'utils/modelName';
import ModelInfoFeaturesDrawer from './ModelInfoFeaturesDrawer';
import ModelAdditionalInfo, { hasAdditionalInfo } from './ModelAdditionalInfo';
import ModelFeaturesList from './ModelFeaturesList';
import useModelFeatures from 'hooks/useModelFeatures';
import { usePackage } from 'contexts/PackageContext';
import { BookLayoutContext } from 'contexts/BookLayoutContext';
import FavouritesIcon from 'components/Favourites/FavouritesIcon';
import { useWindowWidth } from '@react-hook/window-size';

const ModelInfoContainer = styled.div`
    width: 100%;
    padding-left: .25rem;

    .model-header-first-line {
        margin-bottom: ${props => props.$compact ? '.25rem' : '.75rem'};
        padding-right: 0;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 1rem;

        .back-button-container {
            cursor: pointer;
        }

        .favourites-icon-container {
            font-size: 1.25rem;
            padding-right: 2rem;
        }

        h1 {
            font-size: ${props => props.$compact ? '1.5rem' : '1.75rem'};
            line-height: 1.15rem;
            padding-right: 2rem;
            font-weight: ${props => props.theme.FontWeightBold};
            margin: 0;
            white-space: nowrap;
            text-transform: capitalize;

            @media (max-width: 640px) {
                padding-right: 0;
            }
        }
    }

    .model-features {
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        .model-feature {
            display: flex;
            flex-wrap: nowrap;
            margin-right: .5rem;
            font-size: ${props => props.$compact ? '1rem' : '1.1rem'};
            line-height: 1.15em;

            @media (max-width:1200px) {
                font-size: ${props => props.$compact ? '0.9rem' : '1rem'};
            }

            .model-feature-name {
                display: block;
                margin-right: .25rem;
            }
            .model-feature-value {
                font-weight: ${props => props.theme.FontWeightBold};
                display: block;
            }
        }
        .model-feature-more {
            margin-left: .25rem;
            cursor: pointer;
            font-size: 1rem;
        }
    }

    .model-info-extended {
        padding-top: .25rem;

        .model-features {
            margin-left: 0;
            max-width: none;
        }

        .model-additional-info {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            margin-top: .25rem;

            .model-birthdate {
                display: flex;
                flex-wrap: nowrap;
                margin-right: .5rem;
                font-size: ${props => props.$compact ? '1rem' : '1.1rem'};
                line-height: 1.15em;

                @media (max-width:1200px) {
                    font-size: ${props => props.$compact ? '0.9rem' : '1rem'};
                }

                .model-birthdate-name {
                    display: block;
                    margin-right: .25rem;
                }
                .model-birthdate-value {
                    font-weight: ${props => props.theme.FontWeightBold};
                    display: block;
                }
            }
            .model-age {
                display: flex;
                flex-wrap: nowrap;
                margin-right: .5rem;
                font-size: ${props => props.$compact ? '1rem' : '1.1rem'};
                line-height: 1.15em;

                @media (max-width:1200px) {
                    font-size: ${props => props.$compact ? '0.9rem' : '1rem'};
                }

                .model-age-name {
                    display: block;
                    margin-right: .25rem;
                }
                .model-age-value {
                    font-weight: ${props => props.theme.FontWeightBold};
                    display: block;
                }
            }
            .model-customfield {
                display: flex;
                flex-wrap: nowrap;
                margin-right: .5rem;
                font-size: ${props => props.$compact ? '1rem' : '1.1rem'};
                line-height: 1.15em;

                @media (max-width:1200px) {
                    font-size: ${props => props.$compact ? '0.9rem' : '1rem'};
                }

                .model-customfield-name {
                    display: block;
                    margin-right: .25rem;
                }
                .model-customfield-value {
                    font-weight: ${props => props.theme.FontWeightBold};
                    display: block;
                }
            }
        }

    }
`

const ModelInfo = ({ model, compact, mobile }) => {
    const { clientConfig } = useContext(ConfigContext)
    const { features } = clientConfig
    const [featuresDrawerOpen, setFeaturesDrawerOpen] = useState(false);
    const { packageCode } = usePackage();
    const history = useHistory()
    const { modelInfoExtended } = useContext(BookLayoutContext)

    const width = useWindowWidth()
    const isMobile = width < 640;

    const modelIDs = { modelID: model.ModelID, packageModelID: model.ID }

    let modelNameStyle = null;
    if (clientConfig.model && clientConfig.model.nameStyle) {
        modelNameStyle = clientConfig.model.nameStyle;
    }

    const handleMoveToHome = () => {
        if (packageCode) {
            history.push(`/ebook/${packageCode}`);
        } else {
            history.push('/');
        }
    };


    const { modelFeatures, showConversionsButton } = useModelFeatures(model);
    const parseNote = (note) => {
        return note.replace(/'/g, String.fromCharCode(8217))
    }

    const showFeaturesDrawer = () => {
        setFeaturesDrawerOpen(true);
    }

    const hideFeaturesDrawer = () => {
        setFeaturesDrawerOpen(false);
    }

    if (!model) return null;

    //nei layout estesi birthdate, età e custom fields sono già in pagina:
    //lì il drawer serve solo alle conversioni
    const additionalInfoInDrawer = !modelInfoExtended && hasAdditionalInfo(model, clientConfig);

    const ModelFeatures = () => (
        <ModelFeaturesList
            modelFeatures={modelFeatures}
            showConversions={showConversionsButton}
            showAdditionalInfo={additionalInfoInDrawer}
            onShowDetails={showFeaturesDrawer}
        />
    )

    const ModelFeaturesDrawer = () => (
        <ModelInfoFeaturesDrawer
            model={model}
            modelFeatures={modelFeatures}
            features={features}
            clientConfig={clientConfig}
            onClose={hideFeaturesDrawer}
        />
    )

    if (isMobile) {
        return (
            <ModelInfoContainer $compact={compact}>
                <div className="model-header-first-line">
                    <div className="back-button-container">
                        <FontAwesomeIcon icon={light("arrow-left")} onClick={handleMoveToHome} />
                    </div>
                    <h1>{getStyledName(model, modelNameStyle)}</h1>
                    <div className="favourites-icon-container">
                        <FavouritesIcon modelIDs={modelIDs} />
                    </div>
                </div>
                {modelInfoExtended
                    ?
                    <div className='model-info-extended'>
                        <ModelFeatures />
                        <ModelAdditionalInfo model={model} clientConfig={clientConfig} />
                    </div>
                    :
                    <ModelFeatures />
                }
                {model.Socials && model.Socials.length > 0 &&
                    <div className="model-header-second-line">
                        <ModelSocials model={model} />
                    </div>
                }
                {model.Note &&
                    <div className='model-info-right'>
                        <p>&ldquo;{parseNote(model.Note)}&rdquo;</p>
                    </div>
                }
                {featuresDrawerOpen &&
                    <ModelFeaturesDrawer />
                }
            </ModelInfoContainer>
        )
    }

    return (
        <ModelInfoContainer $compact={compact}>
            <div className="model-header-first-line">
                <div className="back-button-container">
                    <FontAwesomeIcon icon={light("arrow-left")} onClick={handleMoveToHome} />
                </div>
                <h1>{getStyledName(model, modelNameStyle)}</h1>
                <div className="favourites-icon-container">
                    <FavouritesIcon modelIDs={modelIDs} />
                </div>
                <div className="model-socials-container">
                    <ModelSocials model={model} />
                </div>
            </div>
            {modelInfoExtended
                ?
                <div className='model-info-extended'>
                    <ModelFeatures />
                    <ModelAdditionalInfo model={model} clientConfig={clientConfig} />
                </div>
                :
                <ModelFeatures />
            }
            {model.note &&
                <div className='model-info-right'>
                    <p>&ldquo;{parseNote(model.Note)}&rdquo;</p>
                </div>
            }
            {featuresDrawerOpen &&
                <ModelFeaturesDrawer />
            }
        </ModelInfoContainer>
    )
}

export default ModelInfo


